// Email-history sync — pulls the SIGNED-IN USER'S OWN correspondence with an
// account's contacts from their mailbox (Microsoft Graph, delegated
// Mail.Read) and shapes it into threads for the API's summarize-and-store
// endpoint. Same delegated-Graph pattern as directorySync.ts: the user only
// ever reads their own mail, and nothing here persists anything — the
// preview modal decides what gets sent, the API stores only summaries.
import { msalInstance } from './msalConfig'
import { signedInAccount } from './ssoMemory'
import type { Contact } from '@/types/entities'

const SCOPES = ['Mail.Read']
const LOOKBACK_MONTHS = 18
const PAGE_SIZE = 50
const MAX_PAGES_PER_CONTACT = 2
const MAX_BODY_CHARS = 6000 // keep POST payloads small; the API re-truncates
const MAX_MESSAGES_PER_THREAD = 25

export interface ThreadMessage {
  from: string
  fromName?: string
  to: string[]
  sentDateTime: string
  body: string
}

export interface EmailThread {
  conversationId: string
  subject: string
  participants: { name?: string; email: string }[]
  contactIds: string[]
  firstMessageAt: string
  lastMessageAt: string
  messageCount: number
  webLink?: string
  messages: ThreadMessage[]
}

async function graphToken(): Promise<string> {
  // The ACTIVE account — the link's person, or whoever just signed in (ssoMemory.ts).
  const account = signedInAccount(msalInstance, msalInstance.getAllAccounts())
  if (!account) throw new Error('No signed-in account')
  try {
    return (await msalInstance.acquireTokenSilent({ account, scopes: SCOPES })).accessToken
  } catch {
    // First Mail.Read consent (or silent failure) — popup keeps the page alive.
    return (await msalInstance.acquireTokenPopup({ account, scopes: SCOPES })).accessToken
  }
}

interface GraphRecipient { emailAddress: { name?: string; address?: string } }
interface GraphMessage {
  id: string
  conversationId: string
  subject: string | null
  from?: GraphRecipient
  toRecipients?: GraphRecipient[]
  ccRecipients?: GraphRecipient[]
  sentDateTime: string
  webLink?: string
  body?: { content?: string }
}

/** The account's contact emails worth searching: external only (internal
 *  staff mail is company-wide noise, not account history). */
export function contactSearchEmails(contacts: Contact[]): Map<string, string> {
  const emailToContactId = new Map<string, string>()
  for (const c of contacts) {
    for (const e of [c.workEmail, c.personalEmail]) {
      const email = e?.trim().toLowerCase()
      if (!email || email.endsWith('@westportproperties.net')) continue
      if (!emailToContactId.has(email)) emailToContactId.set(email, c.id)
    }
  }
  return emailToContactId
}

function sinceIsoDate(): string {
  const d = new Date()
  d.setMonth(d.getMonth() - LOOKBACK_MONTHS)
  return d.toISOString().slice(0, 10)
}

/** Search the user's own mailbox for messages exchanged with any of the
 *  account's contact addresses, grouped into conversation threads. */
export async function fetchAccountThreads(contacts: Contact[]): Promise<EmailThread[]> {
  const emailMap = contactSearchEmails(contacts)
  if (emailMap.size === 0) return []
  const token = await graphToken()
  const since = sinceIsoDate()
  const select = 'id,conversationId,subject,from,toRecipients,ccRecipients,sentDateTime,webLink,body'

  const byId = new Map<string, GraphMessage>()
  for (const email of emailMap.keys()) {
    // KQL participants: matches from/to/cc; received>= bounds the window.
    // $search is quoted as a whole; emails carry no spaces so no inner quoting.
    const search = encodeURIComponent(`"participants:${email} AND received>=${since}"`)
    let url: string | null =
      `https://graph.microsoft.com/v1.0/me/messages?$search=${search}&$select=${select}&$top=${PAGE_SIZE}`
    let pages = 0
    while (url && pages < MAX_PAGES_PER_CONTACT) {
      const res: Response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          // Plain-text bodies — the summarizer doesn't want HTML.
          Prefer: 'outlook.body-content-type="text"',
        },
      })
      if (res.status === 403) throw new Error('Mail access was declined — approve the Mail.Read prompt and retry.')
      if (!res.ok) throw new Error(`Mailbox search failed (${res.status})`)
      const body = (await res.json()) as { value: GraphMessage[]; '@odata.nextLink'?: string }
      for (const m of body.value) if (m.conversationId) byId.set(m.id, m)
      url = body['@odata.nextLink'] ?? null
      pages += 1
    }
  }

  // Group into threads.
  const threads = new Map<string, GraphMessage[]>()
  for (const m of byId.values()) {
    const arr = threads.get(m.conversationId) ?? []
    arr.push(m)
    threads.set(m.conversationId, arr)
  }

  const addr = (r?: GraphRecipient): string => (r?.emailAddress.address ?? '').toLowerCase()
  const out: EmailThread[] = []
  for (const [conversationId, msgs] of threads) {
    msgs.sort((a, b) => a.sentDateTime.localeCompare(b.sentDateTime))
    const participants = new Map<string, string | undefined>()
    const contactIds = new Set<string>()
    for (const m of msgs) {
      for (const r of [m.from, ...(m.toRecipients ?? []), ...(m.ccRecipients ?? [])]) {
        const a = addr(r)
        if (!a) continue
        if (!participants.has(a)) participants.set(a, r?.emailAddress.name ?? undefined)
        const cid = emailMap.get(a)
        if (cid) contactIds.add(cid)
      }
    }
    // Search can surface loosely-matched conversations; keep only threads a
    // tracked contact actually participates in.
    if (contactIds.size === 0) continue
    const recent = msgs.slice(-MAX_MESSAGES_PER_THREAD)
    out.push({
      conversationId,
      subject: msgs[msgs.length - 1].subject ?? '',
      participants: [...participants].map(([email, name]) => (name ? { name, email } : { email })),
      contactIds: [...contactIds],
      firstMessageAt: msgs[0].sentDateTime,
      lastMessageAt: msgs[msgs.length - 1].sentDateTime,
      messageCount: msgs.length,
      webLink: msgs[msgs.length - 1].webLink,
      messages: recent.map((m) => ({
        from: addr(m.from),
        fromName: m.from?.emailAddress.name ?? undefined,
        to: (m.toRecipients ?? []).map(addr).filter(Boolean),
        sentDateTime: m.sentDateTime,
        body: (m.body?.content ?? '').slice(0, MAX_BODY_CHARS),
      })),
    })
  }
  out.sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt))
  return out
}
